import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useToast } from '@raptormaps/toast';
import { Row } from '@raptormaps/layout';
import { Text } from '@raptormaps/text';
import { Icon } from '@raptormaps/icon';
import { FarmInfoCard } from '../FarmDetailCard/FarmInfoCard';
import { SolarFarm } from '../../api/schemas/SolarFarmSchema';
import {
  getFarmPartnersQuery,
  PARTNER_FARM_STATUS_INACTIVE,
  getAddPartnerFarmMutation,
  getUpdatePartnerFarmKeyMutation,
} from '../../api/services/solar-farm-partners';
import { getPartnersQuery } from '../../api/services/partners';
import { NoPartnerView } from './NoPartnerView';
import { PartnerView } from './PartnerView';
import { PartnerEditForm } from './PartnerEditForm';

interface PartnerCardProps {
  farm: SolarFarm;
}

export function PartnerCard({ farm }: PartnerCardProps) {
  const toast = useToast();

  const [isEditing, setIsEditing] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(
    null,
  );
  const [partnerFarmKey, setPartnerFarmKey] = useState('');

  const {
    data: partners,
    isLoading: partnersLoading,
    isError: partnersError,
  } = useQuery(getPartnersQuery());

  const {
    data: farmPartners,
    isLoading: farmPartnersLoading,
    isError: farmPartnersError,
  } = useQuery({
    ...getFarmPartnersQuery(farm.id ?? 0),
    enabled: farm.id !== null,
  });

  const activeLink = (farmPartners ?? []).find(
    link => link.status !== PARTNER_FARM_STATUS_INACTIVE,
  );

  const handleEdit = () => {
    setSelectedPartnerId(activeLink?.partner.id ?? null);
    setPartnerFarmKey(activeLink?.partner_farm_key ?? '');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedPartnerId(null);
    setPartnerFarmKey('');
  };

  const { mutate: updateKey, isLoading: updatingKey } = useMutation(
    getUpdatePartnerFarmKeyMutation(),
  );

  const { mutate: addPartner, isLoading: addingPartner } = useMutation(
    getAddPartnerFarmMutation(),
  );

  const saving = updatingKey || addingPartner;

  const onError = () =>
    toast.error('Failed to save partner. Please try again.');

  const handleSave = () => {
    if (farm.id == null) return;
    if (activeLink) {
      updateKey(
        {
          farmId: farm.id,
          partnerId: activeLink.partner.id,
          partner_farm_key: partnerFarmKey,
        },
        {
          onSuccess: () => {
            toast.success('Partner updated successfully!');
            handleCancel();
          },
          onError,
        },
      );
    } else if (selectedPartnerId !== null) {
      addPartner(
        {
          farmId: farm.id,
          partnerId: selectedPartnerId,
          partner_farm_key: partnerFarmKey,
        },
        {
          onSuccess: () => {
            toast.success('Partner linked successfully!');
            handleCancel();
          },
          onError,
        },
      );
    }
  };

  const canSave = activeLink
    ? partnerFarmKey.trim() !== '' && !saving
    : selectedPartnerId !== null && partnerFarmKey.trim() !== '' && !saving;

  if (farmPartnersLoading) {
    return (
      <FarmInfoCard icon="DashboardSquares" title="Partner">
        <Text variant="paragraph_small" color="#718096">
          Loading...
        </Text>
      </FarmInfoCard>
    );
  }

  if (farmPartnersError) {
    return (
      <FarmInfoCard icon="DashboardSquares" title="Partner">
        <Row gutter="xs" align="center">
          <Icon icon="TriangleExclamation" size="small" color="#dc2626" />
          <Text variant="paragraph_small" color="#dc2626">
            Failed to load partner data.
          </Text>
        </Row>
      </FarmInfoCard>
    );
  }

  return (
    <FarmInfoCard icon="DashboardSquares" title="Partner">
      {isEditing ? (
        <PartnerEditForm
          activeLink={activeLink}
          partners={partners}
          partnersLoading={partnersLoading}
          partnersError={partnersError}
          selectedPartnerId={selectedPartnerId}
          setSelectedPartnerId={setSelectedPartnerId}
          partnerFarmKey={partnerFarmKey}
          setPartnerFarmKey={setPartnerFarmKey}
          saving={saving}
          canSave={canSave}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      ) : activeLink ? (
        <PartnerView activeLink={activeLink} onEdit={handleEdit} />
      ) : (
        <NoPartnerView onAdd={handleEdit} />
      )}
    </FarmInfoCard>
  );
}
